import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cashApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { useDrawer } from '../../contexts/DrawerContext';
import { ManagerStepUpFields, useManagerStepUp, cashError } from './ManagerStepUp';

/** The movements through a drawer. Mirrors `cash.MOVEMENT_KINDS`; the server enforces the rules. */
export const MOVEMENTS = {
  paid_out: { title: 'Paid out', description: 'Cash out of the drawer for a supplier, a taxi, a bag of ice. Say what for.', confirm: 'Record paid-out', amount: true, note: 'required', notePlaceholder: 'What was it for?' },
  safe_drop: { title: 'Safe drop', description: 'A lump out of the drawer into the safe mid-shift. A manager signs it off.', confirm: 'Record drop', amount: true, note: 'optional', manager: true },
  float_top_up: { title: 'Top up the float', description: 'Cash from the safe into the drawer. A manager signs it off.', confirm: 'Record top-up', amount: true, note: 'optional', manager: true },
  tips_out: { title: 'Tips out', description: 'Cash tips taken out of the drawer for the team. A manager signs it off.', confirm: 'Record tips out', amount: true, note: 'optional', manager: true },
  no_sale: { title: 'No sale', description: 'Open the drawer without a sale. Recorded; no money moves.', confirm: 'Open drawer', amount: false, note: 'optional', notePlaceholder: 'Why? (optional)' },
};

/**
 * One dialog for the five movements. `kind` picks the copy and which fields show. Whether
 * a manager is needed: always for safe drop, top-up and tips out; for a paid-out over the
 * venue's `paid_out_manager_over`; never for a no-sale.
 */
const MovementDialog = ({ open, onOpenChange, kind = 'paid_out', onDone, formatCurrency }) => {
  const { user } = useAuth();
  const { canManage, settings, refresh } = useDrawer();
  const copy = MOVEMENTS[kind] || MOVEMENTS.paid_out;
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const amountNum = parseFloat(amount) || 0;
  const paidOutLimit = Number(settings?.paid_out_manager_over ?? 50);
  const needsManager = !canManage && (copy.manager || (kind === 'paid_out' && amountNum > paidOutLimit));
  const stepUp = useManagerStepUp({ open, needed: needsManager, siteId: user?.primary_site });

  useEffect(() => { if (open) { setAmount(''); setNote(''); } }, [open, kind]);

  const amountOk = !copy.amount || (Number.isFinite(amountNum) && amountNum > 0);
  const noteOk = copy.note !== 'required' || note.trim().length > 0;
  const canSubmit = !submitting && amountOk && noteOk && stepUp.ready && !stepUp.noManagers;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await cashApi.movement({ kind, amount: copy.amount ? amountNum : 0, note: note.trim(), ...stepUp.creds() });
      await refresh();
      toast.success(copy.amount ? `${copy.title} · ${formatCurrency(data.movement.amount)}` : 'Drawer opened');
      onDone?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(cashError(err));
      stepUp.clearPin();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="confirm" data-testid="till-movement-dialog" data-kind={kind}>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          {copy.amount && (
            <div className="space-y-1.5">
              <Label className="text-xs">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
                <Input type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00" value={amount}
                  onChange={(e) => setAmount(e.target.value)} autoFocus
                  className="pl-7 h-11 text-xl font-mono-display" data-testid="till-movement-amount" disabled={submitting} />
              </div>
              {kind === 'paid_out' && !canManage && (
                <p className="text-[11px] text-ink-faint">Over {formatCurrency(paidOutLimit)} a manager signs it off.</p>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">{copy.note === 'required' ? 'What for' : 'Note (optional)'}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="h-14 text-sm"
              placeholder={copy.notePlaceholder || 'Anything the cash-up should show'} data-testid="till-movement-note" disabled={submitting} />
          </div>
          <ManagerStepUpFields {...stepUp} disabled={submitting} testId="till-movement" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={submit} disabled={!canSubmit} data-testid="till-movement-confirm">
            {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}{copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MovementDialog;
